// Case Buddy domain module generated from CaseBuddyCore.js.
// Spreadsheet-shaped rows remain the source of truth.
import { buildCaseAddressLegendAnalysis, normalizeCaseAddressLegendAddressKey } from './CaseBuddyAddressLegend.js';
import { caseBuddyHashString, normalizeCell, rowHasMeaningfulData } from './CaseBuddyShared.js';
import { parseCaseEntries } from './CaseBuddySheetData.js';
import { normalizeCaseMatrix } from './CaseBuddyWorkbookState.js';

export const CASE_SUSPICIOUS_ADDRESS_TABLE_HEADERS = Object.freeze(['Address', 'Label', 'Received Amounts']);

export function normalizeCaseSuspiciousHash(value = '') {
  const text = normalizeCell(value);
  if (!text) return '';
  return text.startsWith('0x') || text.startsWith('0X') ? text.toLowerCase() : text;
}

export function standardizeCaseSuspiciousAddressLabel(label = '') {
  let text = normalizeCell(label).replace(/\u2019/g, "'");
  if (!text) return '';
  text = text.replace(/address/gi, 'Address');
  text = text.replace(/^suspect's\s+Address\s*#\s*(\d+)$/i, "Suspect's Address #$1");
  return text;
}

export function isCaseSuspiciousAddressLabel(label = '') {
  return /^Suspect's Address #\d+$/.test(standardizeCaseSuspiciousAddressLabel(label));
}

export function looksLikeCaseSuspiciousNearMissLabel(label = '') {
  const text = normalizeCell(label);
  return !!text && /(suspect|scammer|dormant|address\s*#)/i.test(text) && !isCaseSuspiciousAddressLabel(text);
}

export function getCaseSuspiciousAddressSortNumber(label = '') {
  const match = standardizeCaseSuspiciousAddressLabel(label).match(/#(\d+)$/);
  return match ? Number.parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER;
}

export function parseCaseSuspiciousAmount(value = '') {
  const text = normalizeCell(value).replace(/[$,]/g, '');
  if (!text) return null;
  const match = text.match(/-?\d+(?:\.\d+)?/);
  if (!match) return null;
  const parsed = Number.parseFloat(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
}

export function formatCaseSuspiciousAmount(amount = 0, token = '') {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return '';
  const normalizedToken = normalizeCell(token).toUpperCase();
  const fractionDigits = normalizedToken === 'USDT' || normalizedToken === 'USDC' ? 2 : 4;
  return numeric.toLocaleString('en-US', { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits });
}

export function getCaseSuspiciousAddressLegendEntries(legendAnalysis = {}) {
  if (legendAnalysis.reviewIsCurrent && Array.isArray(legendAnalysis.storedEntries) && legendAnalysis.storedEntries.length > 0) {
    return legendAnalysis.storedEntries;
  }
  if (legendAnalysis.buildIsCurrent && Array.isArray(legendAnalysis.storedEntries) && legendAnalysis.storedEntries.length > 0) {
    return legendAnalysis.storedEntries;
  }
  return Array.isArray(legendAnalysis.entries) ? legendAnalysis.entries : [];
}

export function buildCaseSuspiciousAddressLegendLookup(legendAnalysis = {}) {
  const lookup = new Map();
  getCaseSuspiciousAddressLegendEntries(legendAnalysis).forEach((entry) => {
    const address = normalizeCell(entry?.address || entry?.Address || entry?.preferredDisplayAddress || entry?.PreferredDisplayAddress);
    const label = normalizeCell(entry?.label || entry?.Label || entry?.proposedLegendLabel || entry?.ProposedLegendLabel);
    const key = normalizeCaseAddressLegendAddressKey(address);
    if (key && label && !lookup.has(key)) lookup.set(key, label);
  });
  return lookup;
}

export function addCaseSuspiciousNearMissExample(state = {}, label = '') {
  const text = normalizeCell(label);
  if (!text) return;
  if (!Array.isArray(state.excludedNearMissExamples)) state.excludedNearMissExamples = [];
  if (!state.excludedNearMissExamples.some(existing => existing.toLowerCase() === text.toLowerCase())) {
    state.excludedNearMissExamples.push(text);
  }
}

export function buildCaseSuspiciousReceivedSummaries(tokens = new Map()) {
  const displayLines = [];
  const csvLines = [];
  Array.from(tokens.entries())
    .sort((left, right) => normalizeCell(left[0]).localeCompare(normalizeCell(right[0]), undefined, { sensitivity: 'base' }))
    .forEach(([token, amounts]) => {
      const sortedAmounts = (Array.isArray(amounts) ? amounts : []).slice().sort((left, right) => left - right);
      sortedAmounts.forEach((amount) => {
        const line = `${formatCaseSuspiciousAmount(amount, token)} ${normalizeCell(token)}`.trim();
        displayLines.push(line);
        csvLines.push(line);
      });
    });
  return {
    display: displayLines.join('\\n'),
    cell: displayLines.join('\n'),
    csv: csvLines.join('\n')
  };
}

export function buildCaseSuspiciousAddressTableRowsFromGroups(groups = new Map()) {
  return Array.from(groups.values()).map((group) => {
    const summaries = buildCaseSuspiciousReceivedSummaries(group.tokens);
    const uniqueTransactionCount = group.transactionHashes instanceof Set ? group.transactionHashes.size : 0;
    return {
      key: `suspicious-address-${caseBuddyHashString(`${group.label}|${group.address}`)}`,
      address: normalizeCell(group.address),
      label: normalizeCell(group.label),
      receivedSummaryDisplay: summaries.display,
      receivedSummaryCell: summaries.cell,
      receivedSummaryCsv: summaries.csv,
      sortNumber: group.sortNumber,
      uniqueTransactionCount,
      contributionCount: Number(group.contributionCount) || 0,
      sourceRows: Array.isArray(group.sourceRows) ? group.sourceRows.slice() : [],
      firstRowNumber: group.sourceRows?.[0]?.rowNumber || '',
      searchText: [group.address, group.label, summaries.display, ...(group.sourceRows || []).flatMap(row => [row.rowNumber, row.pathLabel, row.transactionHash, row.amount, row.token])]
        .map(value => normalizeCell(value).toLowerCase())
        .filter(Boolean)
        .join(' ')
    };
  }).sort((left, right) => {
    if (left.sortNumber !== right.sortNumber) return left.sortNumber - right.sortNumber;
    return normalizeCell(left.label).localeCompare(normalizeCell(right.label), undefined, { sensitivity: 'base' });
  });
}

export function buildCaseSuspiciousAddressTableSummary(state = {}) {
  if (!state.legendBuildIsCurrent) return 'Suspicious address table needs the address legend to be built from current Main Data first.';
  if (!state.legendReviewIsCurrent) return 'Suspicious address table needs the address legend to be reviewed for the current case data first.';
  const rowCount = Number(state.rowCount) || 0;
  const txCount = Number(state.uniqueTransactionCount) || 0;
  const contributionCount = Number(state.contributionCount) || 0;
  if (rowCount === 0) {
    return `Suspicious address table is buildable, but no reviewed Address Legend labels matched "Suspect's Address #n". ${txCount} unique transaction${txCount === 1 ? '' : 's'} checked.`;
  }
  let summary = `Suspicious address table is buildable with ${rowCount} suspect address row${rowCount === 1 ? '' : 's'} from ${contributionCount} deduped transfer contribution${contributionCount === 1 ? '' : 's'} across ${txCount} unique transaction${txCount === 1 ? '' : 's'} checked.`;
  if ((state.excludedNearMissLabelCount || 0) > 0) {
    summary += ` ${state.excludedNearMissLabelCount} near-miss label${state.excludedNearMissLabelCount === 1 ? ' was' : 's were'} excluded because they did not exactly match "Suspect's Address #n".`;
  }
  return summary;
}

export function buildCaseSuspiciousAddressTableState(mainData = [], effectiveCurator = {}, workflowState = {}, options = {}) {
  const rows = normalizeCaseMatrix(mainData);
  const legendAnalysis = options.legendAnalysis || buildCaseAddressLegendAnalysis(
    rows,
    effectiveCurator,
    workflowState,
    { labelDormantAddressesSeparately: !!options.labelDormantAddressesSeparately }
  );
  const legendLookup = buildCaseSuspiciousAddressLegendLookup(legendAnalysis);
  const entries = parseCaseEntries(rows);
  const groups = new Map();
  const seenContributionKeys = new Set();
  const uniqueTransactionHashes = new Set();
  const state = {
    caseId: normalizeCell(options.caseId),
    legendBuildIsCurrent: !!legendAnalysis.buildIsCurrent,
    legendReviewIsCurrent: !!legendAnalysis.reviewIsCurrent,
    tableIsCurrent: !!legendAnalysis.buildIsCurrent && !!legendAnalysis.reviewIsCurrent,
    canBuild: !!legendAnalysis.buildIsCurrent && !!legendAnalysis.reviewIsCurrent,
    legendEntryCount: legendLookup.size,
    uniqueTransactionCount: 0,
    rowCount: 0,
    contributionCount: 0,
    sourceRowCount: 0,
    skippedMissingAmountCount: 0,
    skippedMissingTokenCount: 0,
    excludedNearMissLabelCount: 0,
    excludedNearMissExamples: [],
    rows: [],
    sourceRows: [],
    summary: '',
    currentBasisHash: '',
    legendAnalysis
  };

  entries.forEach((entry) => {
    const entryHash = normalizeCaseSuspiciousHash(entry?.hash || '');
    if (entryHash) uniqueTransactionHashes.add(entryHash);

    (entry?.rows || []).forEach((entryRow) => {
      const row = Array.isArray(entryRow?.row) ? entryRow.row : [];
      if (!rowHasMeaningfulData(row)) return;
      const toAddress = normalizeCell(row[3]);
      if (!toAddress) return;
      const addressKey = normalizeCaseAddressLegendAddressKey(toAddress);
      if (!addressKey) return;
      const rawLabel = legendLookup.get(addressKey) || '';
      if (!rawLabel) return;
      const label = standardizeCaseSuspiciousAddressLabel(rawLabel);
      if (!isCaseSuspiciousAddressLabel(label)) {
        if (looksLikeCaseSuspiciousNearMissLabel(rawLabel)) {
          state.excludedNearMissLabelCount += 1;
          addCaseSuspiciousNearMissExample(state, rawLabel);
        }
        return;
      }
      const amount = parseCaseSuspiciousAmount(row[5]);
      if (amount == null) {
        state.skippedMissingAmountCount += 1;
        return;
      }
      const token = normalizeCell(row[6]);
      if (!token) {
        state.skippedMissingTokenCount += 1;
        return;
      }
      const transactionHash = normalizeCaseSuspiciousHash(row[1]) || entryHash || `row-${entryRow.rowIndex}`;
      const contributionKey = [transactionHash, addressKey, String(amount), token.toUpperCase()].join('|');
      if (seenContributionKeys.has(contributionKey)) return;
      seenContributionKeys.add(contributionKey);

      if (!groups.has(label)) {
        groups.set(label, {
          address: toAddress,
          label,
          sortNumber: getCaseSuspiciousAddressSortNumber(label),
          tokens: new Map(),
          transactionHashes: new Set(),
          sourceRows: [],
          contributionCount: 0
        });
      }
      const group = groups.get(label);
      if (!group.tokens.has(token)) group.tokens.set(token, []);
      group.tokens.get(token).push(amount);
      group.transactionHashes.add(transactionHash);
      group.contributionCount += 1;
      const source = {
        rowNumber: Number(entryRow.rowIndex) + 1,
        rowIndex: Number(entryRow.rowIndex),
        pathLabel: normalizeCell(row[0]) || normalizeCell(entry.rows?.[0]?.row?.[0]),
        transactionHash,
        address: toAddress,
        amount,
        token,
        formattedAmount: `${formatCaseSuspiciousAmount(amount, token)} ${token}`.trim(),
        notes: normalizeCell(row[9]),
        attribution: normalizeCell(row[10])
      };
      group.sourceRows.push(source);
      state.sourceRows.push(source);
    });
  });

  state.rows = buildCaseSuspiciousAddressTableRowsFromGroups(groups);
  state.rowCount = state.rows.length;
  state.contributionCount = seenContributionKeys.size;
  state.sourceRowCount = state.sourceRows.length;
  state.uniqueTransactionCount = uniqueTransactionHashes.size;
  state.summary = buildCaseSuspiciousAddressTableSummary(state);
  state.currentBasisHash = `suspicious-address-table-${caseBuddyHashString(JSON.stringify({
    legendBasisHash: legendAnalysis.currentBasisHash || '',
    legendReviewedBasisHash: legendAnalysis.confirmedReviewBasisHash || '',
    rows: state.rows.map(row => [row.address, row.label, row.receivedSummaryDisplay, row.uniqueTransactionCount, row.contributionCount]),
    skippedMissingAmountCount: state.skippedMissingAmountCount,
    skippedMissingTokenCount: state.skippedMissingTokenCount,
    excludedNearMissExamples: state.excludedNearMissExamples
  }))}`;
  return state;
}

export function buildCaseSuspiciousAddressTableDeliverableRows(state = {}) {
  const rows = [CASE_SUSPICIOUS_ADDRESS_TABLE_HEADERS.slice()];
  (Array.isArray(state.rows) ? state.rows : []).forEach((row) => {
    rows.push([
      normalizeCell(row.address),
      normalizeCell(row.label),
      normalizeCell(row.receivedSummaryCell || row.receivedSummaryDisplay)
    ]);
  });
  return rows;
}

export function buildCaseSuspiciousAddressTableAuditRows(state = {}, actionLabel = 'Suspicious address table review') {
  return [
    ['Case Buddy Suspicious Address Table Audit', '', '', '', '', '', '', '', '', 'Audit only. The suspicious address table is generated from Main Data plus the reviewed Address Legend; Main Data is not modified.'],
    ['Action', actionLabel, 'Rows', state.rowCount || 0, 'Deduped Contributions', state.contributionCount || 0, 'Unique Transactions', state.uniqueTransactionCount || 0, 'Basis', state.currentBasisHash || ''],
    ['Status', state.summary || '', 'Legend Current', state.legendBuildIsCurrent ? 'Yes' : 'No', 'Legend Reviewed', state.legendReviewIsCurrent ? 'Yes' : 'No', 'Near Misses Excluded', state.excludedNearMissLabelCount || 0],
    [],
    ...buildCaseSuspiciousAddressTableDeliverableRows(state),
    [],
    ['Source Rows'],
    ['Row', 'Path Label', 'Transaction Hash', 'Address', 'Amount', 'Token', 'Attribution', 'Notes'],
    ...(Array.isArray(state.sourceRows) ? state.sourceRows : []).map(row => [
      row.rowNumber || '',
      row.pathLabel || '',
      row.transactionHash || '',
      row.address || '',
      row.formattedAmount || '',
      row.token || '',
      row.attribution || '',
      row.notes || ''
    ])
  ];
}
